<template>
  <svg :width="width" :height="height" >
    <g v-if="leftLabel" class="label label-left" >
      <text :x="(width - radius * 2) / 4" :y="center[1]" text-anchor="middle" >
        {{ leftLabel }}
      </text>
    </g>
    <g v-if="topLabel" class="label label-top" >
      <text :x="center[0]" :y="center[1] - radius" dy="-20" text-anchor="middle" >
        {{ topLabel }}
      </text>
    </g>
    <g v-if="rightLabel" class="label label-right" >
      <text :x="width - (width - radius * 2) / 4" :y="center[1]" text-anchor="middle" >
        {{ rightLabel }}
      </text>
    </g>
    <g :transform="'translate(' + center[0] + ',' + center[1] + ')'" >
      <g class="band" >
        <path v-for="arc in arcs" :d="arc.arc" :fill="arc.color" />
      </g>
      <g v-if="showNeedle" class="needle" >
        <g :transform="needleTransform" >
          <path :d="needlePath" :fill="needleColor" />
        </g>
        <circle
           cx="0"
           cy="0"
           :r="radius * needleWidthRatio * 2"
           fill="white"
           :stroke-width="radius * 0.01"
           :stroke="needleColor" />
        <text
           v-if="showScore"
           x="0"
           y="0"
           :font-size="radius * fontRatio"
           :dy="radius * 0.12 / 3"
           text-anchor="middle" >
          {{ formatter(currentValue) }}
        </text>
      </g>
    </g>
  </svg>
</template>

<script>
import * as d3 from 'd3'
import TWEEN from '@tweenjs/tween.js'

export default {
  props: {
    width: {
      type: Number,
      default: 500
    },
    height: {
      type: Number,
      default: 500
    },
    min: {
      type: Number,
      default: 0
    },
    max: {
      type: Number,
      default: 1
    },
    colors: {
      type: Array,
      default: () => ['red', 'orange', 'yellow', 'lawngreen', 'green']
    },
    bandRatio: {
      type: Number,
      default: 0.8
    },
    bandPadding: {
      type: Number,
      default: 0.05
    },
    fontRatio: {
      type: Number,
      default: 0.12
    },
    needleWidthRatio: {
      type: Number,
      default: 0.05
    },
    needleHeightRatio: {
      type: Number,
      default: 0.9
    },
    needleColor: {
      type: String,
      default: 'black'
    },
    value: {
      type: Number,
      required: true
    },
    animationDuration: {
      type: Number,
      default: 1000
    },
    animationEasing: {
      type: Function,
      default: TWEEN.Easing.Cubic.Out
    },
    formatter: {
      type: Function,
      default: d => Number.isNaN(d) ? '' : Math.round(d * 10) / 10
    },
    rightLabel: {
      type: String,
      required: false
    },
    leftLabel: {
      type: String,
      required: false
    },
    topLabel: {
      type: String,
      required: false
    },
    hideNeedleWhenOutOfRange: {
      type: Boolean,
      default: false
    },
    showScore: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      currentValue: this.min
    }
  },
  mounted () {
    this.animate()
  },
  watch: {
    value () {
      this.animate()
    }
  },
  computed: {
    showNeedle () {
      if (!this.hideNeedleWhenOutOfRange) {
        return true
      }

      if (Number.isNaN(this.currentValue)) {
        return false
      }
      
      return this.currentValue >= this.min && this.currentValue <= this.max
    },
    scale () {
      return d3
        .scaleLinear()
        .domain([this.min, this.max])
        .range([-90, 90])
    },
    needleTransform () {
      return `rotate(${this.scale(this.currentValue)})`
    },
    needlePath () {
      const area = d3
            .area()
            .x0(d => d.x)
            .y0(d => d.y)
            .x1(d => d.x)
            .y1(d => 0)
      const halfWidth = this.radius * this.needleWidthRatio
      return area([
        {x: -halfWidth, y: 0},
        {x: 0, y: -this.radius * this.needleHeightRatio},
        {x: halfWidth, y: 0}
      ])
    },
    center () {
      return [this.width / 2, this.height * 2 / 3]
    },
    radius () {
      return Math.min(this.width, this.height) / 2
    },
    arcs () {
      const scale = d3
            .scaleBand()
            .domain(this.colors)
            .range([-Math.PI / 2, Math.PI / 2])
            .paddingInner(this.bandPadding)
      return this.colors.map(color => {
        const arc = d3
              .arc()
              .innerRadius(this.radius * this.bandRatio)
              .outerRadius(this.radius)
              .startAngle(scale(color))
              .endAngle(scale(color) + scale.bandwidth())
        return {
          color: color,
          arc: arc()
        }
      })
    }
  },
  methods: {
    animate () {
      const value = {value: this.currentValue}
      const tween = new TWEEN.Tween(value)
            .to({value: this.value}, this.animationDuration)
            .easing(this.animationEasing)
            .onUpdate(() => {
              this.currentValue = value.value
            })
            .start()
      function animate () {
        if (TWEEN.update()) {
          requestAnimationFrame(animate)
        }
      }
      animate()
    }
  }
}
</script>



// WEBPACK FOOTER //
// node_modules/d3vue/components/GaugeMeter.vue